/* eslint-disable max-len */
import React, { useContext, useState, useEffect } from 'react';
import { JobSearchContext } from '../../context';
import { BookmarkedSearch, BookmarkedAlert } from '../../types';
import Collapse from 'react-bootstrap/Collapse';
import classNames from 'classnames';
import TypeaheadSelectBootstrap5 from '../../../components/react/autocompletes/typeahead_select_bootstrap_5';
import Tooltip from '../../../components/react/tooltip/tooltip';
import {
  getBookmarkedSearches,
  createBookmarkedSearch,
  deleteBookmarkSearch,
  createAlert,
  deleteAlert,
  editAlert,
} from '../../../services/bookmarked_searches/bookmarked_searches.service';
import { GENERIC_ERROR_MESSAGE } from '../../../components/react/alert_popup/constants';
import typeaheadStyles from '../../../components/react/autocompletes/generic_typeahead.module.scss';
import { useScrollToResults } from '../../hooks';
import styles from './alerts.module.scss';
/* eslint-enable max-len */

const ALERT_DAILY_INTERVAL = 1;
const ALERT_WEEKLY_INTERVAL = 7;

const Alerts = () => {
  const {
    userInfo: { userId },
    showAlerts,
    setShowAlerts,
    setPageNumber,
    queryParams,
    applySavedSearch,
    searchSelected,
    setSearchSelected,
    setAlertData,
  } = useContext(JobSearchContext);
  const [loading, setLoading] = useState(false);
  const [newAlertName, setNewAlertName] = useState<string>('');
  const [alertInterval, setAlertInterval] = useState<number>(ALERT_WEEKLY_INTERVAL);
  const [savedSearches, setSavedSearches] = useState<BookmarkedSearch[]>([]);
  const scrollToResults = useScrollToResults();

  useEffect(() => {
    setLoading(true);
    getBookmarkedSearches('jobs', 100)
      .then((response) => {
        setSavedSearches(response.data.filter((search) => search.consultant_id === userId));
      })
      .catch(() =>
        setAlertData({
          type: 'error',
          message: 'Oops, we could not retrieve your saved searches. Please try again.',
        })
      )
      .finally(() => setLoading(false));
  }, []);

  // Apply filters from saved searches
  useEffect(() => {
    if (searchSelected.length) {
      setPageNumber(1);
      applySavedSearch(searchSelected[0].params?.filter || {});
      setShowAlerts(false);
      setAlertData({
        type: 'success',
        message: `${searchSelected[0].title} alert successfully loaded`,
      });
    }
  }, [searchSelected]);

  const createNewBookmarkedSearch = () => {
    const params = { filter: { ...queryParams, is_featured: false } };

    return createBookmarkedSearch('jobs', params, newAlertName.trim())
      .then((response) => response)
      .catch((error) => Promise.reject(error));
  };

  const handleCreateNewAlert = async () => {
    setLoading(true);

    try {
      const bookmarkSearchCreated: BookmarkedSearch = await createNewBookmarkedSearch();
      const alertCreated: BookmarkedAlert = await createAlert(
        alertInterval,
        bookmarkSearchCreated.id
      );
      setAlertData({
        type: 'success',
        message: `You have successfully created '${newAlertName.trim()}'`,
      });
      setSavedSearches([...savedSearches, { ...bookmarkSearchCreated, alerts: [alertCreated] }]);
    } catch {
      setAlertData({
        type: 'error',
        message: GENERIC_ERROR_MESSAGE,
      });
    } finally {
      setNewAlertName('');
      setLoading(false);
    }
  };

  const alertNameField = (
    <div>
      <label className='form-label' htmlFor='alertName'>
        Alert Name
        <span className='text-orange-devex'> *</span>
      </label>
      <input
        type='text'
        id='alertName'
        className='form-control rounded-1'
        aria-label='alert name field'
        data-testid='alert-name-input'
        maxLength={40}
        value={newAlertName}
        onChange={({ target }) => setNewAlertName(target.value)}
      />
    </div>
  );

  const intervalButtons = (
    <div className='btn-group w-100' role='group' aria-label='alert interval options'>
      <button
        type='button'
        className={classNames('btn btn-outline-info btn-sm', {
          [styles.intervalSelected]: alertInterval === ALERT_DAILY_INTERVAL,
        })}
        aria-label='daily interval button'
        data-testid='daily-interval-button'
        onClick={() => setAlertInterval(ALERT_DAILY_INTERVAL)}
      >
        <i className='fal icon-calendar-alt me-2' />
        Daily
      </button>
      <button
        type='button'
        className={classNames('btn btn-outline-info btn-sm', {
          [styles.intervalSelected]: alertInterval === ALERT_WEEKLY_INTERVAL,
        })}
        aria-label='weekly interval button'
        data-testid='weekly-interval-button'
        onClick={() => setAlertInterval(ALERT_WEEKLY_INTERVAL)}
      >
        <i className='fal icon-calendar me-2' />
        Weekly
      </button>
    </div>
  );

  const handleCancelAlert = () => {
    setNewAlertName('');
    setAlertInterval(ALERT_WEEKLY_INTERVAL);
    setShowAlerts(false);
  };

  const cancelAlertButton = (
    <button
      type='button'
      className='btn btn-outline-info btn-sm me-2'
      data-testid='cancel-create-alert'
      aria-label='cancel create alert'
      onClick={handleCancelAlert}
    >
      Cancel
    </button>
  );

  const createAlertButton = (
    <button
      type='button'
      className='btn btn-outline-primary btn-sm'
      data-testid='confirm-create-alert'
      aria-label='confirm create new alert'
      disabled={!newAlertName.trim()}
      onClick={handleCreateNewAlert}
    >
      <i className='fal icon-check-circle me-2' />
      <span>Confirm </span> <span className='d-none d-lg-inline'>alert</span>
    </button>
  );

  const searchBar = (
    <TypeaheadSelectBootstrap5
      id='alertsSearch'
      labelKey='title'
      inputProps={{
        'aria-label': 'alerts search field',
        className: classNames('form-control rounded-1', {
          [typeaheadStyles.activeField]: !!searchSelected.length,
        }),
      }}
      placeholder='Search in your alerts'
      onChange={(option: BookmarkedSearch[] | []) => {
        setSearchSelected(option);
        if (!option.length) {
          setPageNumber(1);
          applySavedSearch({});
          scrollToResults();
        }
      }}
      options={savedSearches}
      selected={searchSelected}
    />
  );

  const handleSelectAlert = (alert: BookmarkedSearch) => {
    if (searchSelected.length) return;

    setPageNumber(1);
    setSearchSelected([alert]);
    scrollToResults();
  };

  const handleChangeAlertStatus = (alert: BookmarkedSearch) => {
    setLoading(true);
    if (alert.alerts.length) {
      deleteAlert(alert.alerts[0].id)
        .then(() => {
          if (searchSelected.length && searchSelected[0].id === alert.id) {
            setSearchSelected([
              {
                ...searchSelected[0],
                alerts: [],
              },
            ]);
          }
          const updatedSearches = savedSearches.map((search) => {
            if (alert.id !== search.id) return search;
            return {
              ...search,
              alerts: [],
            };
          });
          setSavedSearches(updatedSearches);
          setAlertData({
            type: 'success',
            message: `You have successfully deactivated '${alert.title}'.`,
          });
        })
        .catch(() => {
          setAlertData({
            type: 'error',
            message: GENERIC_ERROR_MESSAGE,
          });
        })
        .finally(() => setLoading(false));
    } else {
      createAlert(alertInterval, alert.id)
        .then((response) => {
          if (searchSelected.length && searchSelected[0].id === alert.id) {
            setSearchSelected([
              {
                ...searchSelected[0],
                alerts: [response],
              },
            ]);
          }
          const updatedSearches = savedSearches.map((search) => {
            if (alert.id !== search.id) return search;
            return {
              ...search,
              alerts: [response],
            };
          });
          setSavedSearches(updatedSearches);
          setAlertData({
            type: 'success',
            message: `You have successfully activated '${alert.title}'.`,
          });
        })
        .catch(() => {
          setAlertData({
            type: 'error',
            message: GENERIC_ERROR_MESSAGE,
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const handleEditAlertInterval = (alert: BookmarkedSearch, interval: number) => {
    setLoading(true);

    editAlert(alert.alerts[0].id, interval)
      .then(() => {
        if (searchSelected.length && searchSelected[0].id === alert.id) {
          setSearchSelected([
            {
              ...searchSelected[0],
              alerts: [{ ...alert.alerts[0], how_often: interval }],
            },
          ]);
        }
        const updatedSearches = savedSearches.map((search) => {
          if (alert.id !== search.id) return search;
          return {
            ...search,
            alerts: [{ ...alert.alerts[0], how_often: interval }],
          };
        });
        setSavedSearches(updatedSearches);
        setAlertData({
          type: 'success',
          message: `You have successfully updated '${alert.title}'.`,
        });
      })
      .catch(() => {
        setAlertData({
          type: 'error',
          message: GENERIC_ERROR_MESSAGE,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteAlert = (alert: BookmarkedSearch) => {
    setLoading(true);

    deleteBookmarkSearch(alert.id)
      .then(() => {
        setAlertData({
          type: 'success',
          message: `You have successfully deleted '${alert.title}'.`,
        });
        if (searchSelected.length && searchSelected[0].id === alert.id) {
          setSearchSelected([]);
        }
        setSavedSearches(savedSearches.filter((element) => element.id !== alert.id));
      })
      .catch(() => {
        setAlertData({
          type: 'error',
          message: GENERIC_ERROR_MESSAGE,
        });
      })
      .finally(() => setLoading(false));
  };

  const displayAlertInterval = (alert: BookmarkedSearch) => {
    if (!alert.alerts.length) return <i className='text-gray fal icon-calendar' />;
    return alert.alerts[0].how_often === ALERT_DAILY_INTERVAL ? (
      <Tooltip placement='top' message='Daily alerts'>
        <i
          className='text-info fal icon-calendar-alt cursor-pointer'
          data-testid='daily-interval'
          onClick={() => handleEditAlertInterval(alert, 7)}
        />
      </Tooltip>
    ) : (
      <Tooltip placement='top' message='Weekly alerts'>
        <i
          className='text-info fal icon-calendar cursor-pointer'
          data-testid='weekly-interval'
          onClick={() => handleEditAlertInterval(alert, 1)}
        />
      </Tooltip>
    );
  };

  const alertListFiltered = searchSelected.length ? searchSelected : savedSearches;

  const alertList = alertListFiltered.map((alert) => (
    <div className={styles.savedAlert} key={alert.id} data-testid='alert-container'>
      <div>
        <button
          className='btn btn-link text-start text-break'
          data-testid='select-alert'
          onClick={() => handleSelectAlert(alert)}
        >
          {alert.title}
        </button>
      </div>
      <div className={styles.actionIcons}>
        {alert.alerts.length ? (
          <i
            className='text-info fal icon-bell cursor-pointer'
            data-testid='alert-enabled'
            onClick={() => handleChangeAlertStatus(alert)}
          />
        ) : (
          <i
            className='text-info fal icon-bell-slash cursor-pointer'
            data-testid='alert-disabled'
            onClick={() => handleChangeAlertStatus(alert)}
          />
        )}
      </div>
      <div className={styles.actionIcons}>{displayAlertInterval(alert)}</div>
      <div className={styles.actionIcons}>
        <i
          className='text-info fal icon-trash cursor-pointer'
          data-testid='delete-alert'
          onClick={() => handleDeleteAlert(alert)}
        />
      </div>
    </div>
  ));

  return (
    <Collapse in={showAlerts}>
      <div
        className={classNames(styles.jobsSearchAlerts, { loading })}
        data-testid='alerts-container'
        id='alerts-container'
      >
        <small>
          To create an alert, input keywords and filters above to define your search results and
          then select the alert&apos;s name and frequency below.
        </small>
        <div className={styles.newAlertContainer}>
          {alertNameField}
          <div className={styles.buttonsContainer}>
            <div className='d-flex align-items-center'>
              {intervalButtons}
            </div>
            <div className={styles.cancelConfirmButtons}>
              {cancelAlertButton}
              {createAlertButton}
            </div>
          </div>
        </div>
        {!!savedSearches.length && (
          <>
            <div className={styles.alertsDivider} />
            <div className={styles.alertListContainer}>
              {searchBar}
              <div className={styles.headers}>
                <span className='form-label fw-bold'>Alert Name</span>
                <span className='form-label fw-bold text-center'>Status</span>
                <span className='form-label fw-bold text-center'>Frequency</span>
                <span className='form-label fw-bold text-center'>Delete</span>
              </div>
              <div className={styles.alertsContainer}>{alertList}</div>
            </div>
          </>
        )}
      </div>
    </Collapse>
  );
};

export default Alerts;
